import React, { Fragment, useContext, useEffect, useState } from "react";
import TrabajosRetrasados from "../components/analiticas/TrabajosRetrasados";
import CurvaCriticaObra from "../components/analiticas/CurvaCriticaObra";
import ProgresoObra from "../components/analiticas/ProgresoObra";
import GraficaCostos from "../components/finanzas/GraficaCostos";
import { AppConfigContext } from "../context/AppConfigContext";
import { ProyectosContext } from "../context/ProyectosContext";
import { PartidasContext } from "../context/PartidasContext";
import AvanceObra from "../components/analiticas/AvanceObra";
import ChartCard from "../components/analiticas/ChartCard";
import FechaEsperadaTerminacion, {
  INFO_FECHA_ESPERADA,
} from "../components/analiticas/FechaEsperadaTerminacion";
import AvanceFinanciero, {
  INFO_AVANCE_FINANCIERO,
} from "../components/analiticas/AvanceFinanciero";
import ProgramadoVsReal from "../components/analiticas/ProgramadoVsReal";
import AvancesDia from "../components/analiticas/AvancesDia";
import OffCanvas from "../components/global/OffCanvas";
import GanttChart from "../components/charts/GanttChart";
import { MenuContext } from "../context/MenuContext";
import { getValue, fechaRelativa } from "../utils";
import moment from "moment";

const Analiticas = ({ idProyecto }) => {
  const [showAvancesDia, setShowAvancesDia] = useState(false);
  const appconfig = useContext(AppConfigContext);
  const { setSelected } = useContext(MenuContext);
  const { partidas, getPartidasProyecto } = useContext(PartidasContext);
  const {
    proyecto,
    progresoObra,
    ultimoAvance,
    getUltimoAvance,
    getSingleProyecto,
    getProyeccion,
    getAvanceFinanciero,
  } = useContext(ProyectosContext);

  useEffect(() => {
    getPartidasProyecto(idProyecto);
    getSingleProyecto(idProyecto);
    getUltimoAvance(idProyecto);
    getProyeccion(idProyecto);
    getAvanceFinanciero(idProyecto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The obra name as the page heading (same as the cronograma).
  useEffect(() => {
    setSelected({ title: "analiticas", heading: proyecto?.nombre });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyecto?.nombre]);

  const getUltimoDia = () => {
    return moment(proyecto.fecha_fin).diff(proyecto.fecha_inicio, "days");
  };

  // Day / week numbers are 1-based to match the Progreso de Obra chart axis.
  const getDiaSemana = () => {
    if (!progresoObra?.programadoDia) return null;
    const dia = parseInt(proyecto.diaActual);
    if (isNaN(dia) || dia < 0) return null;
    const totalDias = getUltimoDia();
    return {
      dia: `${dia + 1} / ${totalDias + 1}`,
      semana: `${Math.floor(dia / 7) + 1} / ${Math.floor(totalDias / 7) + 1}`,
    };
  };

  const Indicador = ({ icono, label, children, title, action }) => (
    <div className="col-12 col-md mb-2">
      <div
        className="d-flex align-items-center bg-white border rounded-3 px-3 py-2 h-100"
        title={title}
      >
        <i className={`fa ${icono} text-primary me-3`} />
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="small text-muted">{label}</div>
          <div className="bold text-truncate">{children}</div>
        </div>
        {action}
      </div>
    </div>
  );

  const renderUltimoAvance = () => {
    if (ultimoAvance === undefined) {
      return <span className="spinner-border spinner-border-sm" />;
    }
    if (ultimoAvance === null) return "Sin avances";
    return (
      <>
        {fechaRelativa(ultimoAvance.createdAt)}
        {ultimoAvance.usuario && (
          <span className="fw-normal text-muted">
            {" · "}
            <i className="fa fa-user-circle me-1" />
            {ultimoAvance.usuario}
          </span>
        )}
        {ultimoAvance.rol && (
          <span className="badge bg-dark text-capitalize fw-normal ms-2">
            {ultimoAvance.rol}
          </span>
        )}
      </>
    );
  };

  // Keep the cards while progresoObra loads so the row doesn't jump.
  const renderDiaSemana = (diaSemana, key) => {
    if (diaSemana) return diaSemana[key];
    if (!progresoObra?.programadoDia) {
      return <span className="spinner-border spinner-border-sm" />;
    }
    return "-";
  };

  const renderIndicadores = () => {
    const diaSemana = getDiaSemana();
    const detalleAvance = ultimoAvance
      ? [
          moment(ultimoAvance.createdAt).format("DD MMM YYYY HH:mm"),
          ultimoAvance.usuario,
          ultimoAvance.rol,
          ultimoAvance.rubro,
        ]
          .filter(Boolean)
          .join(" · ")
      : undefined;
    return (
      <div className="row mt-2">
        <Indicador
          icono="fa-clock"
          label="Último avance"
          title={detalleAvance}
          action={
            ultimoAvance ? (
              <button
                className="btn btn-sm btn-light border rounded-circle ms-2 flex-shrink-0"
                style={{ width: 32, height: 32, padding: 0 }}
                title="Ver avances del día"
                onClick={() => setShowAvancesDia(true)}
              >
                <i className="fa fa-info text-primary" />
              </button>
            ) : null
          }
        >
          {renderUltimoAvance()}
        </Indicador>
        <Indicador icono="fa-calendar-day" label="Día">
          {renderDiaSemana(diaSemana, "dia")}
        </Indicador>
        <Indicador icono="fa-calendar-week" label="Semana">
          {renderDiaSemana(diaSemana, "semana")}
        </Indicador>
      </div>
    );
  };

  const renderContent = () => {
    if (proyecto && proyecto !== null) {
      return (
        <div
          className="container-fluid h-100"
          style={{ overflowY: "auto", overflowX: "hidden" }}
        >
          {renderIndicadores()}
          {/* 60 / 40: the two projection cards stacked on the left, the
              comparison chart on the right. */}
          <div className="row">
            <div className="col-12 col-lg-60">
              <ChartCard titulo="Fecha Esperada de Terminación" info={INFO_FECHA_ESPERADA}>
                <FechaEsperadaTerminacion />
              </ChartCard>
              <ChartCard titulo="Avance Financiero" info={INFO_AVANCE_FINANCIERO}>
                <AvanceFinanciero />
              </ChartCard>
            </div>
            <div className="col-12 col-lg-40 d-flex flex-column">
              <ChartCard
                titulo="Programado vs Real"
                className="flex-grow-1"
              >
                <ProgramadoVsReal />
              </ChartCard>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-lg-6">
              <ChartCard titulo="Avance de Obra">
                <AvanceObra proyecto={proyecto} />
              </ChartCard>
            </div>
            <div className="col-12 col-lg-6">
              <ChartCard titulo="Ruta Crítica de Obra">
                <CurvaCriticaObra
                  real={proyecto.real}
                  partidas={partidas !== null ? partidas : []}
                  aTiempo={proyecto.real >= proyecto.programado}
                />
              </ChartCard>
            </div>
          </div>
          <ChartCard titulo="Diagrama de Gantt">
            <GanttChart idProyecto={idProyecto} partidas={partidas} />
          </ChartCard>
          <ProgresoObra idProyecto={idProyecto} />
          {getValue(appconfig, "costs_enabled", "boolean") && (
            <GraficaCostos idProyecto={idProyecto} />
          )}
          <ChartCard>
            <TrabajosRetrasados
              idProyecto={idProyecto}
              partidas={partidas !== null ? partidas : []}
              permiso={proyecto.permiso}
            />
          </ChartCard>
        </div>
      );
    }
  };

  return (
    <Fragment>
      {renderContent()}
      <OffCanvas
        title="Últimos avances"
        showOffCanvas={showAvancesDia}
        setShowOffCanvas={setShowAvancesDia}
        style={{ width: "min(640px, 100vw)" }}
      >
        {showAvancesDia && ultimoAvance && (
          <AvancesDia idProyecto={idProyecto} fecha={ultimoAvance.createdAt} />
        )}
      </OffCanvas>
    </Fragment>
  );
};

export default Analiticas;
